/* eslint-disable @typescript-eslint/no-explicit-any */
import { Active, Over } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { ColumnOrderState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { getFixedColumnsNamesFromTableDef } from "@ryde/controls";
import {
  getActiveItemsReports,
  getInactiveItemsReports,
  isOutOfSortReport,
  ITableSettingsDrawerState,
  TableSettingsDrawerInner,
} from "@ryde/layout";
import { useReportId, useResetReportSchema } from "@ryde/redux";
import {
  IColumnDef,
  ITableDto,
  Log,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsVisibilityAF,
  unique,
  VisibilityState,
} from "@ryde/shared";

export interface ITableSettingsDrawerReportsProps {
  tableDefs: IColumnDef<ITableDto, any>[];
}

export const TableSettingsDrawerReports = ({
  tableDefs,
}: ITableSettingsDrawerReportsProps): JSX.Element => {
  const { reportId } = useReportId();
  const { resetReportSchema } = useResetReportSchema();

  const [columnOrder, setColumnOrder] = useRecoilState(
    reportsTableColumnsOrderStateAF(reportId),
  );

  const [columnVisibility, setColumnVisibility] = useRecoilState(
    reportsTableColumnsVisibilityAF(reportId),
  );

  const fixedColumnNames = useMemo(() => {
    return getFixedColumnsNamesFromTableDef(tableDefs);
  }, [tableDefs]);

  const allColumnDefs: IColumnDef<ITableDto, any>[] = useMemo(
    () => tableDefs?.filter(x => !isOutOfSortReport(x)) || [],
    [tableDefs],
  );

  const [state, setState] = useState({
    activeColumns: getActiveItemsReports(
      allColumnDefs,
      columnVisibility,
      columnOrder,
    ),
    inactiveColumns: getInactiveItemsReports(allColumnDefs, columnVisibility),
  });

  useEffect(() => {
    setState({
      activeColumns: getActiveItemsReports(
        allColumnDefs,
        columnVisibility,
        columnOrder,
      ),
      inactiveColumns: getInactiveItemsReports(allColumnDefs, columnVisibility),
    });
  }, [allColumnDefs, columnOrder, columnVisibility]);

  const computedColumnOrder = useEvent(
    (ordered: ColumnOrderState): ColumnOrderState => {
      const updatedColumns = unique([
        ...fixedColumnNames.fixedColumns,
        ...ordered,
        ...fixedColumnNames.lastColumns,
      ]);
      Log.debug("Table settings drawer, set column orders to ", updatedColumns);
      return updatedColumns;
    },
  );

  const onDragEnd = ({
    active,
    over,
  }: {
    active: Active;
    over: Over | null;
  }): void => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = state.activeColumns.findIndex(
      item => item.id === active.id,
    );
    const newIndex = state.activeColumns.findIndex(item => item.id === over.id);

    const newActiveColumns = arrayMove(state.activeColumns, oldIndex, newIndex);
    const ordered = newActiveColumns.map(el => el.id);

    if (ordered.length && setColumnOrder) {
      setColumnOrder(computedColumnOrder(ordered as ColumnOrderState));
    }

    setState(prevState => ({
      ...prevState,
      activeColumns: newActiveColumns,
    }));
  };

  const onVisibilityChange = useEvent(
    (newState: "on" | "off", identifier: string | undefined): void => {
      if (!identifier) {
        Log.debug("Missing column name");
        return;
      }

      let removeFrom =
        newState === "off" ? state.activeColumns : state.inactiveColumns;
      const addTo =
        newState === "off" ? state.inactiveColumns : state.activeColumns;

      const dragItem = removeFrom.find(x => x.id === identifier);
      removeFrom = removeFrom.filter(x => x.id !== identifier);

      if (dragItem) {
        addTo.push(dragItem);
      }

      const newActiveColumns = newState === "off" ? removeFrom : addTo;
      const newInactiveColumns = newState === "off" ? addTo : removeFrom;

      setState({
        activeColumns: newActiveColumns,
        inactiveColumns: newInactiveColumns,
      });

      const updated: VisibilityState = {};
      updated[identifier] = newState === "on";

      if (setColumnVisibility) {
        setColumnVisibility(colVisibility => ({
          ...colVisibility,
          ...updated,
        }));
      }

      if (setColumnOrder) {
        const computedColumns = [
          ...newActiveColumns
            .map(column => column.id)
            .filter(x => x !== undefined),
        ];
        const updatedColumnOrder = computedColumnOrder(computedColumns);
        setColumnOrder(updatedColumnOrder);
      }
    },
  );

  return (
    <TableSettingsDrawerInner
      state={state as ITableSettingsDrawerState<object>}
      onDragEnd={onDragEnd}
      onVisibilityChange={onVisibilityChange}
      onReset={resetReportSchema}
    />
  );
};
